'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { Chain } from 'wagmi/chains';
import { type ChainIdentifier, InterfaceChainUtils, useLocalStorageState } from '@morpho-org/dapp-core';
import { type MorphoClientType, morphoViemExtension } from '@morpho-org/morpho-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMandatoryContext } from '@repo/hooks';
import { type AppKitSSRHandle, useAppKitSSR, useUserAccount, useWalletConnectMetadata } from '@repo/web3';
import { TEMPO_CONNECTOR_ID } from '@repo/web3/utils';
import { getAccount } from '@wagmi/core';
import { createContext, memo, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Address, checksumAddress, type Client } from 'viem';
import { normalize } from 'viem/ens';
// eslint-disable-next-line no-restricted-imports -- this context IS the Vaults app's account adapter: it derives the MorphoAccount abstraction from wagmi's useAccount that useUserAccount-style consumers sit above; screening/auto-disconnect runs upstream in @repo/web3.
import { useAccount, useBytecode, useConfig, useEnsAvatar, useEnsName, useSwitchChain } from 'wagmi';
import { ChainConfig, clientEnvs } from '@/config';
import { getIframeEnvironment } from '@/helpers/iframe';
import { resolveCalldataOrigin, sdkCalldataOrigin, withIntegrationCalldataOrigin } from '@/helpers/transaction';
import { getWalletProvider, isLedgerWalletProvider } from '@/helpers/walletProvider';
import { useRouteType } from '@/hooks/useRouteType';
import { useWeb3Analytics } from '@/lib/analytics/client/hooks/useWeb3Analytics';
import { trackOffchainSignaturesToggled } from '@/lib/analytics/client/trackers/wallet';
import { useWalletConnectionTracker } from '@/lib/observability/client/hooks/useWalletConnectionTracker';
import { logger } from '@/lib/observability/logger';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { resolveSignatureSupport } from './utils/resolveSignatureSupport';
import { isUserRejectedChainSwitch, switchChainWithTimeout } from './utils/switchChain';
export enum SetTargetChainEvent {
  start,
  pending,
  success,
  error,
}
const ALLOW_SIGNATURE_REQUIREMENTS_KEY = 'morpho.allow.signature.requirements';
export type MorphoAccount = Omit<ReturnType<typeof useAccount>, 'address'> & {
  address: Address | undefined;
  ens: string | undefined;
  ensImage: string | undefined;
  isSafe: boolean;
  isLedger: boolean;
  walletConnectPeerName: string | undefined;
  isContract: boolean;
  /** EOA with an EIP-7702 delegation (`0xef0100` code), not a contract account. */
  isDelegatedEoa: boolean;
  /** Bytecode fetch succeeded (or Safe connector); fail-closed gate for signature requirements. */
  isByteCodeResolved: boolean;
  isWalletTypeResolved: boolean;
  isWrongChain: boolean;
};
export type MorphoViemClient = Client & {
  morpho: MorphoClientType;
};

/** The AppKit modal control surface consumed across the app; the canonical type is `AppKitSSRHandle` in @repo/web3. */
export type Web3Modal = AppKitSSRHandle;
export interface Web3ContextProps {
  account: MorphoAccount;
  modal: Web3Modal;
  targetChain: Chain;
  targetChainId: ChainId;
  targetChainIdentifier: ChainIdentifier;
  isMultiChainPage: boolean;
  allowSignatureRequirements: boolean;
  onAllowSignatureRequirementsChange: (allowSignatureRequirements: boolean) => void;
  setTargetChain: React.Dispatch<React.SetStateAction<Chain>>;
  /** `silent` is for automatic syncs with no user gesture: their failures log but never toast. */
  onSetTargetChain: (chain: Chain, options?: {
    silent?: boolean;
  }) => void;
  switchToTargetChain: () => void;
  viemClient: MorphoViemClient;
  /**
   * Build a viem client bound to an arbitrary chain id, extended with the
   * same morpho namespace (and the same `metadata` / `supportSignature`
   * settings) as `viemClient`. Use this whenever a read or tx-build needs to
   * run on a chain other than `targetChain`.
   */
  getClientForChain: (chainId: number) => MorphoViemClient;
}
export const Web3Context = createContext<Web3ContextProps | null>(null);
export const useWeb3Context = () => {
  return useMandatoryContext(Web3Context, "Web3Context");
};
export interface Web3ContextProviderProps extends PropsWithChildren {
  onSetTargetChainEvent?(evt: {
    chain: Chain;
    type: SetTargetChainEvent;
    /** Only set on `error`: the user declined the switch, so there is nothing to surface. */
    isUserRejection?: boolean;
  }): void;
}

/** Rejections and automatic syncs stay at debug; anything else is a real failure worth a warning. */
function logChainSwitchFailure(chainId: number, error: unknown, {
  isUserRejection,
  isAutomatic
}: {
  isUserRejection: boolean;
  isAutomatic: boolean;
}) {
  const context = {
    chainId,
    extra: {
      error: error instanceof Error ? error.message : String(error)
    }
  };
  if (isUserRejection) logger.debug('Chain switch rejected by user', context);else if (isAutomatic) logger.debug('Automatic chain switch failed', context);else logger.warn('Chain switch failed', context);
}
const Web3ContextProvider = ({
  children,
  onSetTargetChainEvent
}: Web3ContextProviderProps) => {
  const {
    isMultiChain,
    chainIdentifier
  } = useRouteType();
  const wagmiAccount = useAccount();
  // The address comes from the screened account (empty while blocked); connection flags stay
  // wagmi's because the adapter forces its own status for Safe.
  const {
    userAddress
  } = useUserAccount();
  const {
    walletConnectPeerName
  } = useWalletConnectMetadata();
  const account = useMemo(() => wagmiAccount.address && !userAddress ? {
    ...wagmiAccount,
    address: undefined,
    addresses: undefined,
    chain: undefined,
    chainId: undefined,
    connector: undefined,
    isConnected: false,
    isConnecting: false,
    isReconnecting: false,
    isDisconnected: true,
    status: 'disconnected'
  } as ReturnType<typeof useAccount> : wagmiAccount, [userAddress, wagmiAccount]);
  // AppKit modal surface (open/disconnect/subscribeState/…) from @repo/web3, SSR-safe.
  const modal = useAppKitSSR();
  const switchChain = useSwitchChain();
  const [allowSignatureRequirements, setAllowSignatureRequirements] = useLocalStorageState(ALLOW_SIGNATURE_REQUIREMENTS_KEY, {
    defaultValue: true
  });
  const onAllowSignatureRequirementsChange = useCallback((value: boolean) => {
    setAllowSignatureRequirements(value);
    trackOffchainSignaturesToggled({
      isEnabled: value
    });
  }, [setAllowSignatureRequirements]);
  const isSafe = account.connector?.type === 'safe';
  const isTempoWallet = account.connector?.id === TEMPO_CONNECTOR_ID;
  // Stamp Ledger-button deposits with a distinct calldata origin for billing reconciliation.
  const isLedger = isLedgerWalletProvider(account.connector);
  // Normalized provider id (e.g. 'ledger') for wallet-connection analytics.
  const walletProvider = getWalletProvider(account.connector);

  // For some reason, wagmi does not immediately detect the user is connected through the Safe connector,
  // even though it has received the account. So the connected status is manually forced.
  const config = useConfig();
  useEffect(() => {
    if (!isSafe || account.address == null) return;
    config.setState(x => ({
      ...x,
      status: 'connected'
    }));
  }, [config, isSafe, account.address]);
  const [targetChain, setTargetChain] = useState<Chain>(() => (chainIdentifier ? InterfaceChainUtils.tryGetChain(chainIdentifier) : undefined) ?? account.chain ?? ChainConfig.DEFAULT_CHAIN);
  const ens = useEnsName({
    address: account.address,
    chainId: targetChain.id
  });
  const normalizedName = useMemo(() => {
    try {
      const ensName = ens.data;
      if (!ensName) return '';
      return normalize(ensName);
    } catch {
      return '';
    }
  }, [ens.data]);
  const ensImage = useEnsAvatar({
    name: normalizedName,
    chainId: targetChain.id
  });

  // A result for any chain but the latest target is stale. Same-chain calls share one wallet
  // request, which stays silent only if every caller asked for that.
  const latestTargetRef = useRef<{
    chainId: number;
    silent: boolean;
  } | null>(null);

  /**
   * Set the target chain and prompt the user to switch to the new chain
   * @param chain - The chain to switch to
   */
  const onSetTargetChain = useCallback(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- intentional narrow deps (account?.address / account?.chain?.id) keep this callback stable across unrelated account changes; this context feeds 178 consumers, so widening to the whole `account` object would recreate it (and the context value) more often. Behaviour is unchanged.
  async (chain: Chain, {
    silent = false
  }: {
    silent?: boolean;
  } = {}) => {
    const {
      address: requestAddress,
      connector: requestConnector
    } = getAccount(config);
    const previous = latestTargetRef.current;
    latestTargetRef.current = {
      chainId: chain.id,
      silent: previous?.chainId === chain.id && switchChain.isPending ? previous.silent && silent : silent
    };
    // Stale once the user targets another chain or another wallet takes over the session.
    const isSuperseded = () => latestTargetRef.current?.chainId !== chain.id || getAccount(config).address !== requestAddress || getAccount(config).connector?.uid !== requestConnector?.uid;
    const isSilent = () => latestTargetRef.current?.silent === true;
    onSetTargetChainEvent?.({
      type: SetTargetChainEvent.start,
      chain
    });
    setTargetChain(chain);

    // Only switch chain if user is connected to the wallet and the chain is different
    if (account?.address && chain.id !== account?.chain?.id && !isSafe) {
      onSetTargetChainEvent?.({
        type: SetTargetChainEvent.pending,
        chain
      });
      if (!switchChain.isPending) {
        try {
          await switchChainWithTimeout(() => switchChain.switchChainAsync({
            chainId: chain.id
          }), switchChain.reset);
          if (isSuperseded() || isSilent()) return;
          onSetTargetChainEvent?.({
            type: SetTargetChainEvent.success,
            chain
          });
        } catch (error: unknown) {
          // A wallet can land on the chain yet leave the request hanging until the timeout.
          if (getAccount(config).chainId === chain.id) {
            logger.debug('Chain switch settled after landing on the target chain', {
              chainId: chain.id
            });
            return;
          }
          const isUserRejection = isUserRejectedChainSwitch(error);
          const isAutomatic = !isSuperseded() && isSilent();
          logChainSwitchFailure(chain.id, error, {
            isUserRejection,
            isAutomatic
          });
          if (isSuperseded() || isAutomatic) return;
          onSetTargetChainEvent?.({
            type: SetTargetChainEvent.error,
            chain,
            isUserRejection
          });
        }
      }
    }
  }, [onSetTargetChainEvent, account?.address, account?.chain?.id, switchChain, isSafe, config]);

  /**
   * Switch the wallet to the current target chain.
   * Convenience wrapper that resolves targetChainId and calls onSetTargetChain.
   */
  const switchToTargetChain = useCallback(() => {
    const chain_0 = InterfaceChainUtils.tryGetChain(targetChain.id);
    if (chain_0) onSetTargetChain(chain_0);
  }, [targetChain.id, onSetTargetChain]);
  const {
    data: byteCode,
    isSuccess: isByteCodeSuccess
  } = useBytecode({
    address: account.address,
    chainId: targetChain.id
  });
  const isContract = isSafe || isDefined(byteCode);
  const isDelegatedEoa = !isSafe && !!byteCode?.toLowerCase().startsWith('0xef0100');
  const isByteCodeResolved = isSafe || isByteCodeSuccess;
  const isWalletTypeResolved = isSafe || !account.address || isByteCodeSuccess;
  const walletType = isWalletTypeResolved ? getWalletType(isSafe, isContract) : undefined;

  // Track wallet connection metrics
  useWalletConnectionTracker({
    account,
    walletType,
    config,
    modal
  });
  const upgradedAccount = useMemo(() => {
    return {
      ...account,
      address: account.address ? checksumAddress(account.address) : undefined,
      // oxlint-disable-next-line typescript/consistent-type-assertions
      ens: ens?.data as string | undefined,
      // oxlint-disable-next-line typescript/consistent-type-assertions
      ensImage: ensImage?.data as string | undefined,
      isSafe,
      isLedger,
      walletConnectPeerName,
      isContract,
      isDelegatedEoa,
      isByteCodeResolved,
      isWalletTypeResolved,
      isWrongChain: !!account?.address && targetChain?.id !== account.chain?.id
    };
  }, [account, ens?.data, ensImage?.data, isSafe, isContract, isDelegatedEoa, isByteCodeResolved, isWalletTypeResolved, isLedger, walletConnectPeerName, targetChain?.id]);
  useWeb3Analytics({
    address: account.address,
    targetChain,
    isSafe,
    isContract: upgradedAccount.isContract,
    walletProvider
  });
  const getClientForChain = useCallback((chainId: number): MorphoViemClient => {
    const calldataOrigin = resolveCalldataOrigin({
      embedderHost: getIframeEnvironment().host,
      isSafeConnector: isSafe,
      isLedgerConnector: isLedger,
      defaultOrigin: clientEnvs.NEXT_PUBLIC_CALLDATA_ORIGIN
    });
    return config.getClient({
      chainId
    }).extend(client => {
      const {
        morpho
      } = morphoViemExtension({
        supportDeployless: true,
        supportSignature: resolveSignatureSupport({
          isSafe,
          isTempoWallet,
          allowSignatureRequirements,
          isByteCodeResolved,
          isContract: upgradedAccount.isContract
        }),
        metadata: {
          origin: sdkCalldataOrigin(calldataOrigin),
          timestamp: true
        }
      })(client);
      return {
        morpho: withIntegrationCalldataOrigin(morpho, calldataOrigin)
      };
    });
  }, [config, isSafe, isLedger, isTempoWallet, upgradedAccount.isContract, isByteCodeResolved, allowSignatureRequirements]);
  const viemClient = useMemo(() => getClientForChain(targetChain.id), [getClientForChain, targetChain.id]);
  const contextValue = useMemo(() => ({
    account: upgradedAccount,
    allowSignatureRequirements,
    onAllowSignatureRequirementsChange,
    modal,
    targetChain,
    targetChainId: targetChain.id,
    targetChainIdentifier: InterfaceChainUtils.getIdentifierById(targetChain.id),
    isMultiChainPage: isMultiChain,
    setTargetChain,
    onSetTargetChain,
    switchToTargetChain,
    viemClient,
    getClientForChain
  }), [modal, targetChain, setTargetChain, onSetTargetChain, switchToTargetChain, upgradedAccount, viemClient, getClientForChain, isMultiChain, allowSignatureRequirements, onAllowSignatureRequirementsChange]);
  return <Web3Context.Provider value={contextValue}>{children}</Web3Context.Provider>;
};
export default memo(Web3ContextProvider);